import { useAlgoliaSearchStore } from '@/stores/Category/AlgoliaSearchStore';
import { AlgoliaFilterType } from '@/types/Algolia/AlgoliaFilterType';
import { AlgoliaSortingOption } from '@/types/Algolia/AlgoliaSortingOption';
import _ from 'lodash';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

export const useAlgoliaProductStore = defineStore(
  `algolia-product-${window.store_code}`,
  () => {
    const { t } = useI18n();
    const searchStore = useAlgoliaSearchStore();

    const productIndex = window.product_index;
    const enableAlgoliaFilters = window.enable_algolia_filters;

    const availableFilters: AlgoliaFilterType[] = [
      {
        name: t('pages.category.product_filters.categories_name'),
        type: 'categories',
        filterAttribute: 'categories.name',
        prismicIdentifier: 'category',
      },
      {
        name: t('pages.category.product_filters.material_name'),
        type: 'checkbox-list',
        filterAttribute: 'material',
        prismicIdentifier: 'material',
      },
      {
        name: t('pages.category.product_filters.tags_audience_name'),
        type: 'checkbox-list',
        filterAttribute: 'tags.audience',
        prismicIdentifier: 'audience',
      },
      {
        name: t('pages.category.product_filters.tags_color_name'),
        type: 'checkbox-list',
        filterAttribute: 'tags.color',
        prismicIdentifier: 'color',
      },
      {
        name: t('pages.category.product_filters.tags_lifestyle_name'),
        type: 'checkbox-list',
        filterAttribute: 'tags.lifestyle',
        prismicIdentifier: 'lifestyle',
      },
      {
        name: t('pages.category.product_filters.tags_theme_name'),
        type: 'checkbox-list',
        filterAttribute: 'tags.theme',
        prismicIdentifier: 'theme',
      },
    ];
    const filters: Ref<AlgoliaFilterType[]> = ref([]);

    const sortingOptions: AlgoliaSortingOption[] = [
      {
        value: `${productIndex}_popular_desc`,
        label: t('pages.category.sort.popular'),
        isDefaultOption: true,
      },
      {
        value: `${productIndex}_price_asc`,
        label: t('pages.category.sort.price_asc'),
        isDefaultOption: false,
      },
      {
        value: `${productIndex}_price_desc`,
        label: t('pages.category.sort.price_desc'),
        isDefaultOption: false,
      },
      {
        value: `${productIndex}_name_asc`,
        label: t('pages.category.sort.name_asc'),
        isDefaultOption: false,
      },
      {
        value: `${productIndex}_name_desc`,
        label: t('pages.category.sort.name_desc'),
        isDefaultOption: false,
      },
    ];

    async function retrieveAvailableFilters() {
      if (!enableAlgoliaFilters) {
        return;
      }

      const order =
        (await searchStore.getSettings()).renderingContent?.facetOrdering
          ?.facets?.order ?? null;
      if (!order) {
        console.debug(
          'No facet display available, using all available filters'
        );
        filters.value = availableFilters;
        return;
      }

      _.each(order, (facet: string) => {
        const filter = availableFilters.find(f => f.filterAttribute === facet);
        if (filter) {
          filters.value.push(filter);
        }
      });
    }

    return {
      productIndex,
      enableAlgoliaFilters,
      retrieveAvailableFilters,
      filters,
      sortingOptions,
    };
  }
);
